<template>
  <div class="flex w-full">
    <div class="relative mt-1 ml-2 w-full">
      <div v-if="!alternateDesign" class="text-common-grey whitespace-nowrap">
        {{ label }}
      </div>
      <div
        :class="[{ 'overflow-visible': add_company_mode }, { 'overflow-x-auto': !add_company_mode }]"
        class="relative flex w-full py-2 pb-3 z-1">
        <div v-if="!add_company_mode" id="selected-companies-wrapper" class="flex gap-x-2">
          <div v-for="(company, i) in companies_list" :key="company.id" class="relative">
            <div class="flex items-center gap-x-2">
              <div class="relative flex items-center gap-x-2">
                <!-- In playbook case -->
                <Avatar v-if="typeof company == 'string'" :hover-effect="true" :first-name="company" :last-name="null" />
                <Avatar
                  v-else
                  :is-company="true"
                  :owner="company.id.includes('company')"
                  :hover-effect="true"
                  :tooltip="$utils.getRecordName(company) ? $utils.getRecordName(company) : Record.from(company).info?.name"
                  :first-name="$utils.getRecordName(company) ? $utils.getRecordName(company) : Record.from(company).info?.name"
                  :last-name="null" />

                <div
                  v-if="!disabled"
                  id="remove-company-button"
                  class="absolute w-4 h-4 text-xs text-red-400 border rounded-full cursor-pointer bg-light-screen-background-grey dark:bg-dark-screen-background dark:border-dark-divider-border z-1 -top-2 -right-1"
                  @click="handleRemoveCompany(i)">
                  <span class="ml-[4px] relative bottom-[2px]">x</span>
                </div>
              </div>

              <div v-if="!multiSelect && !hideName" class="dark:text-white max-w-44 truncate whitespace-nowrap">
                {{ $utils.getRecordName(company) ? $utils.getRecordName(company) : Record.from(company).info?.name }}
              </div>
            </div>
          </div>
        </div>

        <div
          class="relative justify-center mr-2 text-center align-middle cursor-pointer dark:border-dark-divider-border h-9 w-9 z-1"
          :class="[companies_list?.length ? 'ml-2' : '']">
          <div v-if="!disabled && !(companies_list.length == 1 && !multiSelect)" class="relative">
            <div
              id="plus-icon"
              :class="[{ 'bg-white has-tooltip': alternateDesign }]"
              class="w-8 h-8 border border-dotted rounded-full dark:border-dark-divider-border"
              @click="add_company_mode = !add_company_mode">
              <Plus class="absolute w-5 h-5 text-common-grey mt-[5px] ml-[5px]" />
              <span
                class="tooltip whitespace-nowrap absolute mt-4 inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                {{ label }}</span>
            </div>
          </div>

          <div
            v-if="add_company_mode"
            id="companies-auto-complete"
            class="mt-2 min-h-[50px] text-left w-[500px] relative z-[999]"
            :class="[
              `${openPosition == 'center' ? 'relative right-[250px]' : openPosition == 'left' ? 'relative right-[500px]' : ''}`,
              { 'w-[300px]': shortKing }
            ]">
            <CompaniesMultiSelectAutoCompleteInput
              :open-to-top="openToTop"
              :short-king="shortKing"
              :type-filter="getTypeFilter()"
              :companies="companies_list"
              type="allTypes"
              :auto-focus="true"
              :get-opportunities="getOpportunities"
              :data="contacts_list"
              :label="label"
              :show-label="true"
              value-attr="name"
              label-attr="name"
              :is-box-background="true"
              :autocomplete-options="autocompleteOptions"
              @chosen="handleAddCompany($event)"
              @close="add_company_mode = false"
              @reset="
                () => {
                  add_company_mode = false
                }
              " />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Plus } from "lucide-vue-next"
import CompaniesMultiSelectAutoCompleteInput from "@/components/shared/CompaniesMultiSelectAutoCompleteInput.vue"

import Avatar from "@/components/shared/Avatar.vue"
import { Record } from "@/models/record"

export default {
  components: { Plus, CompaniesMultiSelectAutoCompleteInput, Avatar },
  props: {
    alternateDesign: null,
    hideName: null,
    openToTop: null,
    shortKing: {
      type: Boolean,
      default: false
    },
    getOpportunities: {
      type: Boolean,
      default: true
    },
    typeFilter: {
      type: String,
      required: false
    },
    label: {
      type: String,
      required: false
    },
    companies: {
      type: Array,
      required: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    multiSelect: {
      type: Boolean,
      default: true
    },
    openPosition: {
      type: String,
      default: "right"
    },
    autocompleteOptions: {
      type: Array,
      required: false
    }
  },
  emits: ["done", "changed"],
  data() {
    return {
      add_company_mode: false,
      companies_list: [],
      contacts_list: [],
      dont_trigger: false,
      prevent_watch: false,
      Record
    }
  },
  computed: {},
  watch: {
    companies_list: {
      deep: true,
      handler() {
        if (!this.dont_trigger) {
          this.$emit("changed", this.companies_list)
        }
      }
    },
    companies: {
      deep: true,
      handler() {
        if (!this.prevent_watch) {
          this.dont_trigger = true
          setTimeout(() => {
            this.dont_trigger = false
          }, 500)
          this.companies_list = []
          this.companies.forEach((company) => {
            if (company?.id) {
              this.companies_list.push(company)
            } else {
              if (typeof company == "string" && company.includes("$")) {
                this.companies_list.push(company)
              } else {
                if (this.$store.state.data.records_fetched[company]) {
                  this.companies_list.push(this.$store.state.data.records_fetched[company])
                } else {
                  this.$store.dispatch("data/getRecord", company).then((res) => {
                    this.companies_list.push(res.data)
                  })
                }
              }
            }
          })
        }
      }
    }
  },
  created() {
    if (this.companies?.length) {
      this.prevent_watch = true
      setTimeout(() => {
        this.prevent_watch = false
      }, 1000)
      this.companies_list = []
      this.companies.forEach((company) => {
        if (company.id) {
          if (!this.companies_list.find((c) => c.id == company.id)) {
            this.companies_list.push(company)
          }
        } else {
          if (typeof company == "string" && company.includes("$")) {
            this.companies_list.push(company)
          } else {
            this.$store.dispatch("data/getRecord", company).then((res) => {
              if (!this.companies_list.find((c) => c.id == res.data.id)) {
                this.companies_list.push(res.data)
              }
            })
          }
        }
      })
    }
  },
  methods: {
    getTypeFilter() {
      if (this.typeFilter) {
        return this.typeFilter
      }
      if (this.$route.params?.object_type) {
        return this.$route.params?.object_type
      }
      if (this.$route.params.page == "company" || this.$route.params.page == "opportunity") {
        return this.$route.params.page
      }
      return null
    },
    handleRemoveCompany(i) {
      this.companies_list.splice(i, 1)
    },

    handleAddCompany(e) {
      if (e?.label && e?.value) {
        this.companies_list.push(e.value)
      } else {
        this.companies_list.push(e)
      }
      this.add_company_mode = false
    }
  }
}
</script>
